import React, { FC, useEffect, useState } from 'react';
import LocalModal from 'components/base/local-modal';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import { toast } from 'react-toastify';
import { formatRecurringOrdersDate, handleApiError } from 'utils/functions';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS } from 'utils/constants';
import { OrderType } from 'models/order.model';
import useOrdersContext from 'contexts/orders.context';

type CancelRecurrentOrderModalProps = {
  show: boolean;
  onClose: () => void;
  orderCode: string;
};

const CancelRecurrentOrderModal: FC<CancelRecurrentOrderModalProps> = ({
  show,
  onClose,
  orderCode
}) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [selectedOrderCodes, setSelectedOrderCodes] = useState<string[]>([]);

  const { activeCompany } = useDashboardContext();
  const { handleReset } = useOrdersContext();
  const navigate = useNavigate();
  const companyId = activeCompany.id;
  useEffect(() => {
    fetchAllOrders();
  }, []);
  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`scheduled-order/recurring-orders`, {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        params: {
          order_code: orderCode
        }
      });
      if (res.data.data) {
        setOrders(res.data.data.results);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };
  const onConfirm = async () => {
    const data = {
      order_codes: selectedOrderCodes,
      company_id: companyId
    };
    setLoading(true);
    try {
      const res = await apiService.put('business/recurring-orders/cancel', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data
      });
      if (res.status === 200) {
        handleReset();
        toast(<ToastMessage type='success' message={res.data.message} />);
        setSelectedOrderCodes([]);
        onClose();
        navigate(`/${ROUTE_KEYS.ORDERS}`);
      }
    } catch (err) {
      handleApiError(err, 'Failed to cancel order');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, order_code: string) => {
    if (!e.target.checked) {
      setSelectedOrderCodes(selectedOrderCodes.filter((code) => code !== order_code));
    } else {
      setSelectedOrderCodes([...selectedOrderCodes, order_code]);
    }
  };
  return (
    <LocalModal
      isOpen={show}
      onRequestClose={onClose}
      title='Cancel Order '
      contentClassName=' sm:!max-w-[400px]'>
      <div>
        {orders
          .filter((order) => !order.cancelled)
          .map((order) => (
            <div
              key={order.id}
              className='pl-2 pr-2 h-[40px] mt-3 border-b-[0.8px] border-b-[#E7E8E9] flex items-center justify-between gap-4'>
              <label htmlFor='' className='text-sm'>
                {formatRecurringOrdersDate(order?.scheduled_delivery_time)}
              </label>
              <input
                type='checkbox'
                className='accent-[#e60a2b] w-4 h-4 rounded'
                onChange={(e) => handleChange(e, order.order_code)}
                checked={selectedOrderCodes.includes(order.order_code)}
              />
            </div>
          ))}
        <div className='mt-5 flex justify-end'>
          <Button
            color='transparent'
            variant='block'
            className='mr-2 w-[139px] h-[41px]'
            onClick={onClose}>
            Cancel
          </Button>
          <Button
            color='primary'
            variant='block'
            className='w-[139px] h-[41px]'
            loading={loading}
            onClick={onConfirm}>
            Confirm
          </Button>
        </div>
      </div>
    </LocalModal>
  );
};

export default CancelRecurrentOrderModal;
